// Copy for the two-page D2D Launch proposal (menu → D2D Launch), written for
// go-to-market clients adding door-to-door as a new channel: they need the
// field sales leader and the infrastructure, not just reps. Pricing figures
// come live from the shared pricing settings.

import type { SystemStage } from './partnershipBrief'

export const launchHeadline = {
  title: 'Launch door-to-door as a channel built for traction.',
  lead: 'You already have a healthy business. D2D Launch stands up the whole channel for you: the infrastructure, the field sales leader and the team, sequenced so it gains momentum instead of stalling out.',
}

export const launchProblems = [
  {
    title: 'No proven playbook.',
    body: 'Building field sales by trial and error burns cash and months you never get back.',
  },
  {
    title: 'No leader to own it.',
    body: 'Without the right field operator, a new channel never gets off the ground.',
  },
  {
    title: "Can't staff the team.",
    body: "Recruiting can't put reps in the field fast enough to build real momentum.",
  },
]

// The launch sequence on page 1: what happens at each stage, what D2D HIRE
// runs and what the partner's team owns.
export const launchSystem: SystemStage[] = [
  {
    when: 'Implementation',
    title: 'Build the infrastructure',
    what: 'Comp, CRM, onboarding, scripts and KPIs: the operating system of a real channel.',
    we: ['Comp plan and onboarding design', 'CRM, scripts, ATS and ads built'],
    you: ['Sign-off on comp and pay'],
  },
  {
    when: 'Months 1–2',
    title: 'Find the leader',
    what: 'The field operator who owns the territory and drives the number.',
    we: ['Leader search and vetting', 'Judged by D2D operators'],
    you: ['Final say on the leader'],
  },
  {
    when: 'Month 3',
    title: 'Build the team',
    what: 'Ads go live and the recruiting engine staffs reps, month after month.',
    we: ['Ads, creative and funnel', 'Live screening, 7 days a week'],
    you: ['Running interviews', 'Every hiring decision'],
  },
  {
    when: 'Month 4+',
    title: 'Scale',
    what: 'New markets, more leadership and output that compounds as the channel matures.',
    we: ['Weekly leader coaching', 'Sales School for D2D HIRE reps'],
    you: ['Field onboarding', 'Culture'],
  },
]

// With a corporate recruiter, D2D HIRE runs the interviews too.
export const launchSystemWithRecruiter: SystemStage[] = [
  launchSystem[0],
  launchSystem[1],
  {
    ...launchSystem[2],
    we: ['Ads, creative and funnel', 'Screening and interviews'],
    you: ['Every hiring decision'],
  },
  launchSystem[3],
]

export const launchLeaderSpotlight = {
  eyebrow: 'The leader search · Included with D2D Launch',
  title: 'The right operator makes or breaks a new channel.',
  body: "We find and vet the field sales leader who can own the field and hit the number from day one, judged by people who've run D2D teams themselves. You always make the final call.",
  stats: [
    { value: '2–8 weeks', label: 'to a signed leader' },
    { value: 'Since 2004', label: 'recruiting D2D reps' },
    { value: 'Your call', label: 'on the final hire' },
  ],
}

export const launchOutcomes = [
  {
    title: 'A complete channel infrastructure',
    body: 'Everything the channel runs on, built and installed for you, not pieced together over months.',
  },
  {
    title: 'The right field sales leader',
    body: 'An operator who owns the field and the number, found before your first rep starts.',
  },
  {
    title: 'A predictable rep pipeline',
    body: 'The recruiting engine behind 10,000+ D2D reps, pointed at your new channel.',
  },
  {
    title: 'Reps ready to sell',
    body: 'Every D2D HIRE rep goes through our live Sales School before they knock a door.',
  },
]



// Implementation column of the page 2 timeline for a launch.
export const launchBuildPhase = {
  window: 'Implementation',
  phase: 'Infrastructure',
  items: [
    'Comp plan and onboarding designed with you',
    'CRM, scripts, ATS and ads built',
    'Leader profile defined',
  ],
}
